import { Stack, Tag, TagLabel, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiTag } from "react-icons/fi";
import { DynamicPurchasingSystemType, type Public, Regulation } from "../../autogen/bff-api";
import { useCpvCodes } from "../../common/cpv/useCpvCodes";
import { LabelValueGrid } from "../sourcing-events/portal/view-single/LabelValueGrid";
import { frameworkCompetitionRows } from "../sourcing-events/portal/view-single/frameworkCompetitionRows";
import { SectionCard } from "../sourcing-events/portal/view-single/SectionCard";

const DASH = "–";

export const ClassificationSection = ({ publicContext: pc }: { publicContext: Public }) => {
  const { t, i18n } = useTranslation();
  const { codeIndex } = useCpvCodes();
  const isNorwegian = i18n.language.startsWith("no");
  const yesNo = (v?: boolean | null) => (v == null ? DASH : v ? t("Yes") : t("No"));
  // A NONE enum value means "none selected"; render it as "No" to match the
  // wizard summary, rather than leaking the enum's own "None" label.
  const noneAsNo = (value: string | null | undefined, none: string) =>
    value && value !== none ? t(value) : t("No");
  const cpv = pc.cpvCodes ?? [];

  return (
    <SectionCard icon={FiTag} title={t("Classification and rules")}>
      <Stack spacing="5">
        <Stack spacing="1">
          <Text color="smMuted" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            {t("CPV codes")}
          </Text>
          {cpv.length > 0 ? (
            <Wrap>
              {cpv.map((code) => {
                const dto = codeIndex[code];
                const name = dto ? (isNorwegian ? dto.nameNb : dto.nameEn) : null;
                return (
                  <WrapItem key={code}>
                    <Tag colorScheme="teal" borderRadius="full">
                      <TagLabel whiteSpace="normal">
                        {code}
                        {name ? ` — ${name}` : ""}
                      </TagLabel>
                    </Tag>
                  </WrapItem>
                );
              })}
            </Wrap>
          ) : (
            <Text color="smText" fontSize="sm">
              {DASH}
            </Text>
          )}
        </Stack>
        <LabelValueGrid
          rows={[
            { label: t("Regulation"), value: pc.regulation ? t(pc.regulation) : DASH },
            {
              label: pc.regulation === Regulation.Forsyningsforskriften ? t("Applicable part of the regulation") : t("FOA part"),
              value: pc.foaPart ? t(pc.foaPart) : DASH,
            },
            ...frameworkCompetitionRows(pc, t),
            {
              label: t("Dynamic purchasing system"),
              value: noneAsNo(pc.dynamicPurchasingSystem, DynamicPurchasingSystemType.None),
            },
            { label: t("Electronic auction"), value: yesNo(pc.electronicAuction) },
            { label: t("GPA coverage"), value: yesNo(pc.gpaCoverage) },
            { label: t("EU financing"), value: yesNo(pc.euFunded) },
            { label: t("Accelerated procedure"), value: yesNo(pc.acceleratedProcedure) },
          ]}
        />
      </Stack>
    </SectionCard>
  );
};
